/**
 * Builds the ordered list of action button IDs shown in account details.
 *
 * @param {string} category - Account category (for example: `deposits`, `metals`, `stocks`).
 * @param {string} displayedComponent - Currently visible component (for example: `table`, `grid`, `settings`).
 * @param {string|null} [displayedData=null] - Active data tab inside the component (for example: `holdings`, `cashflow`, `quotes`, `settings`).
 * @returns {string[]} Ordered button IDs consumed by `ButtonBar`.
 *
 * Import is enabled for (as of 2026-03-04):
 * - holdings: deposits, stocks, loans, realEstate
 * - quotes: theoretically all categories
 */
export default function getButtonsToShow(category, displayedComponent, displayedData = null) {
  if (!category || !displayedComponent) {
    console.error('getButtonsToShow: required argument missing. Called with category, displayedComponent', category, displayedComponent);
  }

  // ButtonBar knows to only display 'save' together with 'grid'

  switch (category) {
    // deposits have no quotes
    case 'deposits':
      switch (displayedComponent) {
        case 'grid':
          return ['holdings', 'settings', 'save', 'cancel', 'table', 'grid', 'import'];
        case 'settings':
          return ['holdings', 'settings'];
        default:
          return ['holdings', 'settings', 'table', 'grid', 'import', 'sync'];
      }

    case 'loans':
      switch (displayedComponent) {
        case 'settings':
          return ['holdings', 'settings'];
        default:
          return ['holdings', 'settings', 'table'];
      }

    // metals: import is not enabled for holdings, have 'historicalValueCalc' in grid
    case 'metals':
    case 'objectsOfValue':
      switch (displayedComponent) {
        case 'grid':
          return ['holdings', 'quotes', 'cashflow', 'settings', 'save', 'cancel', 'table', 'grid'].concat(category === 'metals' ? 'historicalValueCalc' : []);
        case 'settings':
          return ['holdings', 'quotes', 'cashflow', 'settings'];
        // handle table component
        default:
          if (displayedData === 'cashflow') return ['holdings', 'cashflow', 'quotes', 'settings', 'table'];
          if (displayedData === 'quotes') return ['holdings', 'cashflow', 'quotes', 'settings', 'table', 'grid', 'import'];
          return ['holdings', 'quotes', 'cashflow', 'settings', 'table', 'grid'];
      }

    // there is a wizard instead of grid
    case 'unlistedShares':
      switch (displayedData) {
        case 'holdings':
        case 'quotes': {
          const buttons = ['holdings', 'cashflow', 'quotes', 'settings', 'table', 'wizard'];
          return displayedComponent === 'grid' ? buttons.concat(['save', 'cancel']) : buttons;
        }
        default:
          return ['holdings', 'cashflow', 'quotes', 'settings'];
      }

    // stocks have import for holdings and quotes
    case 'stocks':
      switch (displayedComponent) {
        case 'grid':
          return ['holdings', 'cashflow', 'quotes', 'settings', 'table', 'grid', 'import', 'save', 'cancel'];
        case 'settings':
          return ['holdings', 'cashflow', 'quotes', 'settings'];
        // handle table component
        default:
          if (displayedData === 'cashflow') return ['holdings', 'cashflow', 'quotes', 'settings'];
          return ['holdings', 'cashflow', 'quotes', 'settings', 'table', 'grid', 'import'];
      }

    // no holdings
    case 'realEstate':
      switch (displayedData) {
        case 'settings':
        case 'cashflow':
          return ['cashflow', 'quotes', 'settings'];
        default:
          if (displayedComponent === 'grid') return ['cashflow', 'quotes', 'settings', 'table', 'grid', 'save', 'cancel'];
          return ['cashflow', 'quotes', 'settings', 'table', 'grid', 'import'];
      }

    case 'pension':
      switch (displayedData) {
        case 'settings':
        case 'cashflow':
          return ['cashflow', 'quotes', 'settings'];
        case 'quotes':
          if (displayedComponent === 'grid') return ['cashflow', 'quotes', 'settings', 'table', 'grid', 'save', 'cancel'];
          return ['cashflow', 'quotes', 'settings', 'table', 'grid', 'import'];
        default:
          return ['cashflow', 'quotes', 'settings', 'table'];
      }

    default:
      return ['save'];
  }
}
